import { NextPage } from 'next';

import { PageLayout } from '@klokhuis/core/PageLayout';
import { ROUTES } from '@klokhuis/core/Routing';

import { RealEstateInsightsContent } from './components/RealEstateInsightsContent';

const ORIGIN = 'https://www.findmyprops.com';
const PAGE_URL = `${ORIGIN}/real-estate-insights`;

const INSIGHTS_JSON_LD = [
  {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    url: PAGE_URL,
    name: 'Real Estate Insights — FindMyProps',
    description:
      'Expert guides on buying, selling, RERA, GST, stamp duty, home loans, PMAY, agricultural land rules, and market trends across Tamil Nadu, Andhra Pradesh, Telangana, Karnataka, and Kerala.',
    publisher: { '@type': 'Organization', name: 'FindMyProps', url: ORIGIN },
    inLanguage: 'en-IN',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'South Indian Real Estate Guides',
    url: PAGE_URL,
    numberOfItems: 10,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Complete Step-by-Step Guide to Buying Property in South India',
        url: PAGE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: "Seller's Complete Guide: How to Sell Property in South India",
        url: PAGE_URL,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'RERA in South India: A Complete State-by-State Guide',
        url: PAGE_URL,
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: 'GST on Real Estate in South India: What Every Buyer Must Know',
        url: PAGE_URL,
      },
      {
        '@type': 'ListItem',
        position: 5,
        name: 'PMAY: How South Indian Homebuyers Can Benefit from the Housing Scheme',
        url: PAGE_URL,
      },
      {
        '@type': 'ListItem',
        position: 6,
        name: 'Stamp Duty and Registration Charges Across South Indian States',
        url: PAGE_URL,
      },
      {
        '@type': 'ListItem',
        position: 7,
        name: 'Buying Agricultural and Farm Land in South India: Rules, Restrictions, and Process',
        url: PAGE_URL,
      },
      {
        '@type': 'ListItem',
        position: 8,
        name: 'Home Loan Guide for South Indian Property Buyers',
        url: PAGE_URL,
      },
      {
        '@type': 'ListItem',
        position: 9,
        name: 'Property Approval Bodies in South India: DTCP, CMDA, HMDA, BDA Explained',
        url: PAGE_URL,
      },
      {
        '@type': 'ListItem',
        position: 10,
        name: 'South Indian Real Estate Market: Structural Trends and Key Investment Themes',
        url: PAGE_URL,
      },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What documents are required to buy property in Tamil Nadu?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Key documents for buying property in Tamil Nadu include: EC (Encumbrance Certificate) for minimum 13 years from the Sub-Registrar office; Patta in the seller\'s name from the Tahsildar; Chitta and Adangal for agricultural land; DTCP or CMDA layout approval depending on location; original Sale Deed chain; and current property tax receipts. After registration, the buyer should apply for Patta transfer at the Tahsildar\'s office.',
        },
      },
      {
        '@type': 'Question',
        name: 'What is GST on under-construction flats in Tamil Nadu, Andhra Pradesh, and Telangana?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Under-construction residential flats attract GST at 1% for affordable housing and 5% for non-affordable housing (rates effective from April 2019, without Input Tax Credit). Affordable housing is defined as: carpet area up to 60 sqm in metro cities (Chennai, Bengaluru, Hyderabad) or up to 90 sqm in non-metro cities (Coimbatore, Madurai, Vijayawada, etc.), AND price not exceeding ₹45 lakh. Ready-to-move-in properties where the Occupancy Certificate has been received before the sale attract no GST.',
        },
      },
      {
        '@type': 'Question',
        name: 'What is stamp duty in Tamil Nadu and Andhra Pradesh for property registration?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'In Tamil Nadu, stamp duty is approximately 7% of the guideline value (whichever is higher between guideline value and actual sale price), plus a registration charge of approximately 1% (capped for residential properties). In Andhra Pradesh, stamp duty is approximately 5% plus a transfer duty of 1.5% and registration charge of 1%, making the total approximately 7.5%. Telangana charges approximately 4% stamp duty plus 1.5% transfer duty and 0.5% registration. These rates are indicative — verify with the respective Sub-Registrar\'s office.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can a non-agriculturist buy agricultural land in Tamil Nadu and Karnataka?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'In Tamil Nadu, the transfer of agricultural land to non-agriculturists is generally restricted under the Tamil Nadu Land Reforms Act. Specific permissions may be required. In Karnataka, Sections 79A and 79B of the Karnataka Land Reforms Act historically prohibited non-agriculturists from purchasing agricultural land; the Karnataka Land Reforms (Amendment) Act 2020 liberalised these restrictions, but the legal position has been subject to litigation and review — buyers must verify the current prevailing law with a Karnataka-based property advocate. Always obtain a specific legal opinion before purchasing agricultural land in any South Indian state.',
        },
      },
      {
        '@type': 'Question',
        name: 'What is the TNRERA and how do buyers verify a project is RERA registered in Tamil Nadu?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'TNRERA is the Tamil Nadu Real Estate Regulatory Authority, constituted under the RERA Act 2016. Buyers can verify whether a real estate project is registered with TNRERA by visiting the official portal at rera.tn.gov.in and searching by project name, promoter name, or RERA registration number. A registered project will show its approved plans, completion timeline, units sold and available, and quarterly progress reports. Purchasing from an unregistered project (where registration is mandatory) is illegal and deprives buyers of RERA protections.',
        },
      },
      {
        '@type': 'Question',
        name: 'What is the difference between DTCP and CMDA approval in Tamil Nadu?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'CMDA (Chennai Metropolitan Development Authority) approves layouts and buildings within the Chennai Metropolitan Area (approximately 1,189 sq km covering Chennai and parts of surrounding districts). DTCP (Directorate of Town and Country Planning) approves layouts in all other areas of Tamil Nadu outside the Chennai Metropolitan Area. For any plot purchase in Tamil Nadu, buyers must verify whether the layout has the appropriate CMDA or DTCP sanction depending on the location. Banks will not finance plots in unapproved layouts.',
        },
      },
      {
        '@type': 'Question',
        name: 'What are the RBI home loan LTV ratios applicable to property buyers in India?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'The RBI prescribes maximum Loan-to-Value (LTV) ratios for home loans: For loans up to ₹30 lakh, banks can finance up to 90% (buyer pays 10% down payment). For loans between ₹30 lakh and ₹75 lakh, banks can finance up to 80% (buyer pays 20%). For loans above ₹75 lakh, banks can finance up to 75% (buyer pays 25%). LTV is calculated on the lower of the property\'s market value or registered value.',
        },
      },
    ],
  },
];

const RealEstateInsights: NextPage = () => {
  return (
    <PageLayout
      pageTitle="Real Estate Insights | South India Property Guides — FindMyProps"
      metaDescription="Expert buyer guides, seller guides, RERA, GST, stamp duty, PMAY, home loans, and market trend articles specific to Tamil Nadu, Andhra Pradesh, Telangana, Karnataka, and Kerala real estate."
      route={ROUTES.RealEstateInsights}
      showPropertyLinksGrid={false}
      jsonLd={INSIGHTS_JSON_LD}
    >
      <PageLayout.MainRaw>
        <RealEstateInsightsContent />
      </PageLayout.MainRaw>
    </PageLayout>
  );
};

export default RealEstateInsights;
